import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Home, ChevronDown } from "lucide-react";
import useNav from "@/hooks/useNav";
import { useI18n } from "@/lib/i18n";
import CategoryIcon from "@/components/common/CategoryIcon";

/** Iter 704 — category names follow the site language (339 names come
 * translated from GET /api/i18n/categories, falling back to English). */
function CatName({ name }) {
  const { tCat, t } = useI18n();
  const viaCat = tCat(name);
  // Category map first; anything it doesn't know still translates
  // through the UI-string bundle.
  return <>{viaCat !== name ? viaCat : t(name)}</>;
}

const BADGE_CLS = {
  NEW: "bg-emerald-500 text-white",
  HOT: "bg-red-600 text-white",
};

function Badge({ badge, testId }) {
  if (!badge || !BADGE_CLS[badge]) return null;
  return (
    <sup
      data-testid={testId}
      className={`ml-1 inline-flex items-center rounded-full text-[9px] font-black px-1.5 py-0.5 leading-none ${BADGE_CLS[badge]}`}
    >
      {badge}
    </sup>
  );
}

/**
 * Route helper: `type: "deal"` / `"event"` roots link straight to the deals
 * or events page (no dropdown); regular ad roots browse to `/c/<slug>`.
 */
function routeFor(cat) {
  if (cat.type === "deal") return "/deals";
  if (cat.type === "event") return "/events";
  return `/c/${cat.slug}`;
}

/**
 * Iter-219 header category bar — API-driven order, admin-managed labels +
 * badges, width-driven "More ▾" overflow with a mega popover.
 *
 * We measure the actual rendered width of every category button once and
 * then, whenever the container resizes, decide how many items fit inline
 * (leaving room for the "More ▾" pill). Categories that don't fit get
 * grouped into the More popover — one column block per hidden category with
 * its subcategories underneath. An "Indian Products" shortcut is appended
 * to the More popover per spec.
 */
export default function CategoryNavBar() {
  const q = useNav();
  // NAV-228: render categories in the EXACT order served by the backend.
  // The admin Navigation Manager controls ordering — no client-side sort,
  // no hardcoded pin lists. Prior founder-pin block removed on 2026-02-28.
  const categories = q.data?.categories || [];

  const scopeRef = useRef(null);
  const innerRef = useRef(null); // The max-w-7xl inner container — the real budget for the visible items.
  const measurerRef = useRef(null);
  const measurements = useRef([]);
  const [visibleCount, setVisibleCount] = useState(categories.length);
  const [containerWidth, setContainerWidth] = useState(0);
  const [openId, setOpenId] = useState(null); // hovered/open category dropdown
  const [moreOpen, setMoreOpen] = useState(false);

  // Measure each item once by rendering a hidden mirror row — reliable and
  // resilient to font-loading (we re-run on font readiness).
  useLayoutEffect(() => {
    if (!measurerRef.current) return;
    const items = measurerRef.current.querySelectorAll("[data-nav-measure]");
    measurements.current = Array.from(items).map((el) => el.getBoundingClientRect().width);
  }, [categories]);

  // Observe the *inner* max-w-7xl container width (not the full-bleed nav
  // element) so the overflow measurement reflects the actual space the
  // visible items can occupy. Extra visual gutter is added below to force
  // a centered visual group.
  useEffect(() => {
    if (!innerRef.current) return undefined;
    setContainerWidth(innerRef.current.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setContainerWidth(e.contentRect.width);
    });
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);

  const MORE_PILL_WIDTH = 80; // reserved for the "More ▾" button
  const HOME_WIDTH = 40;
  const GAP = 4;
  // Reserve visual gutters inside the container so the visible category
  // group clusters near the middle — the founder's "central" requirement.
  // ~10% of the container on each side keeps a comfortable margin without
  // pushing too many items into the More popover.
  const VISUAL_GUTTER = 0.1;

  useEffect(() => {
    if (!containerWidth || !measurements.current.length) {
      setVisibleCount(categories.length);
      return;
    }
    let used = HOME_WIDTH + GAP;
    let count = 0;
    // Effective budget: the inner container width, minus a visual gutter so
    // the group clusters visibly in the middle even when the raw total
    // would otherwise fill the full container.
    const effective = containerWidth * (1 - VISUAL_GUTTER);
    // Reserve space for More ▾ *unless* everything fits in the effective
    // budget without it.
    const totalNeeded =
      HOME_WIDTH + GAP + measurements.current.reduce((s, w) => s + w + GAP, 0);
    const reserveMore = totalNeeded > effective;
    const budget = effective - (reserveMore ? MORE_PILL_WIDTH + GAP : 0);
    for (let i = 0; i < measurements.current.length; i++) {
      const w = measurements.current[i] + GAP;
      if (used + w > budget) break;
      used += w;
      count += 1;
    }
    setVisibleCount(Math.max(0, Math.min(count, categories.length)));
  }, [containerWidth, categories.length]);

  const visible = useMemo(() => categories.slice(0, visibleCount), [categories, visibleCount]);
  const overflow = useMemo(() => categories.slice(visibleCount), [categories, visibleCount]);
  const overflowActive = overflow.length > 0;

  // Close dropdowns on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenId(null);
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close open menus if user clicks outside.
  useEffect(() => {
    const onClick = (e) => {
      if (!scopeRef.current) return;
      if (!scopeRef.current.contains(e.target)) {
        setOpenId(null);
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleDropdown = useCallback((c) => {
    if (c.type === "deal" || c.type === "event") return; // linked directly
    setOpenId((prev) => (prev === c.id ? null : c.id));
  }, []);

  return (
    <nav
      className="w-full bg-white border-b border-soft relative"
      data-testid="category-nav"
      aria-label="Category navigation"
      ref={scopeRef}
    >
      {/* Hidden measurer — never displayed, only used to measure natural widths.
        Wrapped in a zero-size overflow-hidden container so its whitespace-
        nowrap content doesn't expand the document width on narrow viewports
        (bug found in iter-38: 390px viewport → 1765px scrollWidth). */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", top: 0, left: 0 }}
      >
        <div
          ref={measurerRef}
          className="whitespace-nowrap flex items-center gap-1"
        >
          {categories.map((c) => (
            <button
              key={c.id}
              data-nav-measure
              className="inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm font-semibold"
            >
              {<CatName name={c.label || c.name} />}
              {c.type === "ad" && <ChevronDown className="w-3.5 h-3.5" />}
              <Badge badge={c.badge} />
            </button>
          ))}
        </div>
      </div>

      <div ref={innerRef} className="max-w-7xl mx-auto px-2 md:px-8">
        <ul
          className="flex items-center gap-2 md:gap-3 py-2 justify-start md:justify-center"
          data-testid="category-nav-items"
        >
          <li>
            <Link
              to="/"
              aria-label="Home"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-700 hover:bg-[color:var(--brand-blue-wash)] hover:text-[color:var(--brand-blue)]"
              data-testid="catnav-home"
            >
              <Home className="w-4 h-4" />
            </Link>
          </li>

          {visible.map((c) => {
            const hasKids = c.type === "ad" && (c.children || []).length > 0;
            const isOpen = openId === c.id;
            const cls = `inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              isOpen
                ? "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]"
                : "text-slate-700 hover:bg-[color:var(--brand-blue-wash)] hover:text-[color:var(--brand-blue)]"
            }`;
            return (
              <li
                key={c.id}
                className="relative shrink-0"
                onMouseEnter={() => hasKids && setOpenId(c.id)}
                onMouseLeave={() => hasKids && setOpenId((cur) => (cur === c.id ? null : cur))}
                data-testid={`catnav-item-${c.slug}`}
              >
                {hasKids ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleDropdown(c)}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      data-testid={`catnav-btn-${c.slug}`}
                      className={cls}
                    >
                      <Link
                        to={routeFor(c)}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center"
                      >
                        {<CatName name={c.label || c.name} />}
                      </Link>
                      <ChevronDown className="w-3.5 h-3.5" />
                      <Badge badge={c.badge} testId={`catnav-badge-${c.slug}`} />
                    </button>
                    {isOpen && (
                      <div
                        className="absolute left-0 top-full mt-1 min-w-[240px] rounded-2xl bg-white shadow-lg border border-soft py-2 z-40"
                        data-testid={`catnav-dropdown-${c.slug}`}
                      >
                        {(c.children || []).map((sc) => (
                          <Link
                            key={sc.id}
                            to={`/c/${sc.slug}`}
                            data-testid={`catnav-sub-${sc.slug}`}
                            className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <span className="w-6 h-6 rounded-md bg-[color:var(--brand-blue-wash)] grid place-items-center text-[color:var(--brand-blue)] shrink-0 overflow-hidden">
                              <CategoryIcon
                                name={sc.icon || c.icon}
                                thumbnailUrl={sc.thumbnail_url}
                                emoji={sc.emoji || c.emoji}
                                alt={sc.name}
                                className="w-4 h-4"
                              />
                            </span>
                            <span className="flex-1 min-w-0 truncate"><CatName name={sc.name} /></span>
                            <Badge badge={sc.badge} />
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={routeFor(c)}
                    data-testid={`catnav-btn-${c.slug}`}
                    className={cls}
                  >
                    {<CatName name={c.label || c.name} />}
                    <Badge badge={c.badge} testId={`catnav-badge-${c.slug}`} />
                  </Link>
                )}
              </li>
            );
          })}

          {overflowActive && (
            <li className="relative shrink-0" data-testid="catnav-more">
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                aria-expanded={moreOpen}
                aria-haspopup="true"
                data-testid="catnav-more-btn"
                className={`inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm font-semibold ${
                  moreOpen
                    ? "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]"
                    : "text-slate-700 hover:bg-[color:var(--brand-blue-wash)] hover:text-[color:var(--brand-blue)]"
                }`}
              >
                <CatName name="More" /> <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {moreOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-[min(90vw,720px)] rounded-2xl bg-white shadow-lg border border-soft p-4 z-40 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4"
                  data-testid="catnav-more-panel"
                >
                  {overflow.map((c) => (
                    <div key={c.id} className="min-w-0">
                      <Link
                        to={routeFor(c)}
                        onClick={() => setMoreOpen(false)}
                        data-testid={`catnav-more-heading-${c.slug}`}
                        className="flex items-center gap-2 font-display font-bold text-sm text-slate-900 mb-1.5 hover:text-[color:var(--brand-blue)]"
                      >
                        <span className="w-7 h-7 rounded-lg bg-[color:var(--brand-blue-wash)] grid place-items-center text-[color:var(--brand-blue)] shrink-0 overflow-hidden">
                          <CategoryIcon
                            name={c.icon}
                            thumbnailUrl={c.thumbnail_url}
                            emoji={c.emoji}
                            alt={c.name}
                            className="w-4 h-4"
                          />
                        </span>
                        <span className="min-w-0 truncate">
                          {<CatName name={c.label || c.name} />}
                          <Badge badge={c.badge} />
                        </span>
                      </Link>
                      {c.type === "ad" && (c.children || []).length > 0 && (
                        <ul className="space-y-1 pl-9">
                          {c.children.map((sc) => (
                            <li key={sc.id}>
                              <Link
                                to={`/c/${sc.slug}`}
                                onClick={() => setMoreOpen(false)}
                                data-testid={`catnav-more-sub-${sc.slug}`}
                                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-[color:var(--brand-blue)]"
                              >
                                <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">
                                  <CategoryIcon
                                    name={sc.icon || c.icon}
                                    thumbnailUrl={sc.thumbnail_url}
                                    emoji={sc.emoji || c.emoji}
                                    alt={sc.name}
                                    className="w-4 h-4"
                                  />
                                </span>
                                <span className="truncate"><CatName name={sc.name} /></span>
                                <Badge badge={sc.badge} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {/* Spec: append Indian Products shortcut at the end.
                      Iter-274e W2: use the backend geo-aware redirect
                      instead of the internal /indian-products page. */}
                  <div className="min-w-0">
                    <a
                      href="/api/go/bazaar"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMoreOpen(false)}
                      data-testid="catnav-more-indian-products"
                      className="block font-display font-bold text-sm text-slate-900 mb-1.5 hover:text-[color:var(--brand-blue)]"
                    >
                      <CatName name="Indian Products" />
                      <Badge badge="NEW" />
                    </a>
                    <p className="text-xs text-slate-500">
                      Shop authentic products from local Indian sellers.
                    </p>
                  </div>
                </div>
              )}
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}
